import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import calculadorasDinamicasApi from "../../api/calculadorasDinamicas/calculadorasDinamicasApi";
import { apiGetConsecutivos } from "../../api/consecutivoApi";

const emptyForm = {
  id: null,
  nombre: "",
  descripcion: "",
  sexo_aplica: "A",
  edad_min: 0,
  edad_max: 200,
  estado: 1,
  orden_visual: 0,
  schema_version: 2,
  formula_js: "",
  result_config: {
    preset: "numeric",
    mode: "value_unit",
    label: "Resultado",
    sample_value: "",
    unit: "",
    message: "",
    secondary_title: "Interpretación",
    secondary_value: "",
    help: "",
  },
  variables: [],
};

const emptySourceForm = {
  id: null,
  codigo: "",
  nombre: "",
  descripcion: "",
  tipo_fuente: "master_api_resultado",
  consecutivo_id: "",
  api_route: "patients/result-latest",
  response_field: "Data_APIM1",
  tipo_dato: "number",
  estado: 1,
  orden: 0,
  allow_manual_override_default: 1,
};

export const useCalculadorasDinamicas = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [sourceCatalog, setSourceCatalog] = useState([]);
  const [selectedSourceId, setSelectedSourceId] = useState(null);
  const [sourceForm, setSourceForm] = useState(emptySourceForm);
  const [consecutivos, setConsecutivos] = useState([]);

  const normalizeVariable = (variable = {}, index = 0) => ({
    id: variable.id ?? null,
    variable_key: variable.variable_key || "",
    label: variable.label || "",
    field_type: variable.field_type || "text",
    source_type: variable.source_type || "manual",
    source_key: variable.source_key || "",
    source_config_id: variable.source_config_id ?? null,
    required: Number(variable.required ?? 0),
    auto_fill: Number(variable.auto_fill ?? 0),
    editable:
      variable.editable === undefined ? 1 : Number(variable.editable ?? 0),
    visible: variable.visible === undefined ? 1 : Number(variable.visible ?? 0),
    prefill_mode: variable.prefill_mode || "none",
    allow_manual_override:
      variable.allow_manual_override === undefined
        ? 0
        : Number(variable.allow_manual_override ?? 0),
    refresh_strategy: variable.refresh_strategy || "on_open",
    default_value: variable.default_value || "",
    options_json: variable.options_json || "",
    layout_json: variable.layout_json || "",
    orden:
      variable.orden === undefined || variable.orden === null
        ? index
        : Number(variable.orden) || 0,
  });

  const normalizeResultConfig = (config = {}) => ({
    ...emptyForm.result_config,
    ...(config && typeof config === "object" ? config : {}),
  });

  const load = async () => {
    try {
      const rs = await calculadorasDinamicasApi.getAll({
        estado: estadoFiltro || undefined,
        search: search || undefined,
      });
      setItems(rs.data || []);
    } catch (e) {
      toast.error("Error cargando calculadoras dinámicas");
    }
  };

  const loadSources = async () => {
    try {
      const rs = await calculadorasDinamicasApi.getSources({ estado: 1 });
      setSourceCatalog(rs.data || []);
    } catch (e) {
      toast.error("Error cargando fuentes de calculadoras");
    }
  };

  const loadConsecutivos = async () => {
    try {
      const rs = await apiGetConsecutivos();
      setConsecutivos(rs.data || []);
    } catch (e) {
      toast.error("Error cargando consecutivos");
    }
  };

  useEffect(() => {
    load();
    loadSources();
    loadConsecutivos();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      load();
    }, 250);
    return () => clearTimeout(t);
  }, [search, estadoFiltro]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) => (i.nombre || "").toLowerCase().includes(s));
  }, [items, search]);

  const handleSelect = async (row) => {
    try {
      setSelected(row.id);
      const rs = await calculadorasDinamicasApi.getById(row.id);
      if (rs.success) {
        const incoming = rs.data || {};
        setForm({
          ...emptyForm,
          ...incoming,
          schema_version: 2,
          formula_js: incoming.formula_js || "",
          result_config: normalizeResultConfig(incoming.result_config),
          variables: Array.isArray(incoming.variables)
            ? incoming.variables.map((variable, index) =>
                normalizeVariable(variable, index)
              )
            : [],
        });
      } else {
        toast.error(rs.message || "Error cargando detalle");
      }
    } catch (e) {
      toast.error("Error cargando detalle");
    }
  };

  const handleNew = () => {
    setSelected(null);
    setForm(emptyForm);
  };

  const handleSelectSource = async (sourceId) => {
    try {
      setSelectedSourceId(sourceId);
      const rs = await calculadorasDinamicasApi.getSourceById(sourceId);
      if (rs.success) {
        setSourceForm({ ...emptySourceForm, ...(rs.data || {}) });
      } else {
        toast.error(rs.message || "Error cargando fuente");
      }
    } catch (e) {
      toast.error("Error cargando fuente");
    }
  };

  const handleNewSource = () => {
    setSelectedSourceId(null);
    setSourceForm(emptySourceForm);
  };

  const addVariable = () => {
    setForm((prev) => ({
      ...prev,
      variables: [
        ...(prev.variables || []),
        normalizeVariable(
          {
            variable_key: "",
            label: "",
            field_type: "text",
            source_type: "manual",
            source_key: "",
            source_config_id: null,
            required: 0,
            auto_fill: 0,
            editable: 1,
            visible: 1,
            prefill_mode: "none",
            allow_manual_override: 0,
            refresh_strategy: "on_open",
            default_value: "",
            options_json: "",
            layout_json: "",
          },
          (prev.variables || []).length
        ),
      ],
    }));
  };

  const updateVariable = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      variables: (prev.variables || []).map((variable, currentIndex) =>
        currentIndex === index ? { ...variable, ...patch } : variable
      ),
    }));
  };

  const removeVariable = (index) => {
    setForm((prev) => ({
      ...prev,
      variables: (prev.variables || [])
        .filter((_, currentIndex) => currentIndex !== index)
        .map((variable, currentIndex) => ({
          ...variable,
          orden: currentIndex,
        })),
    }));
  };

  const save = async () => {
    if (!form.nombre.trim()) {
      toast.warn("El nombre es obligatorio");
      return;
    }
    if (!["M", "F", "A"].includes(form.sexo_aplica)) {
      toast.warn("Sexo aplica no válido");
      return;
    }
    const edadMin = Number(form.edad_min);
    const edadMax = Number(form.edad_max);
    if (Number.isNaN(edadMin) || Number.isNaN(edadMax)) {
      toast.warn("Edades deben ser numéricas");
      return;
    }
    if (edadMin < 0 || edadMin > 200 || edadMax < 0 || edadMax > 200) {
      toast.warn("Edades fuera de rango");
      return;
    }
    if (edadMin > edadMax) {
      toast.warn("La edad mínima no puede ser mayor que la edad máxima");
      return;
    }
    const estado = Number(form.estado);
    if (Number.isNaN(estado) || ![0, 1].includes(estado)) {
      toast.warn("Estado no válido");
      return;
    }

    const schemaVersion = 2;
    if (Number(form.schema_version ?? 2) !== 2) {
      toast.warn("La calculadora debe usar esquema V2");
      return;
    }

    const variables = Array.isArray(form.variables) ? form.variables : [];
    const seenKeys = new Set();
    for (let index = 0; index < variables.length; index += 1) {
      const variable = variables[index];
      const variableKey = (variable.variable_key || "").trim();
      const label = (variable.label || "").trim();

      if (!variableKey) {
        toast.warn(`La variable ${index + 1} debe tener clave interna`);
        return;
      }

      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(variableKey)) {
        toast.warn(`La clave ${variableKey} no es válida`);
        return;
      }

      if (seenKeys.has(variableKey)) {
        toast.warn(`La clave ${variableKey} está repetida`);
        return;
      }
      seenKeys.add(variableKey);

      if (!label) {
        toast.warn(`La variable ${variableKey} debe tener etiqueta`);
        return;
      }

      if (variable.options_json?.trim()) {
        try {
          JSON.parse(variable.options_json);
        } catch (error) {
          toast.warn(`Las opciones JSON de ${variableKey} no son válidas`);
          return;
        }
      }

      if (variable.layout_json?.trim()) {
        try {
          JSON.parse(variable.layout_json);
        } catch (error) {
          toast.warn(`El layout JSON de ${variableKey} no es válido`);
          return;
        }
      }

      if (
        variable.source_type === "api" &&
        (!variable.source_config_id || Number(variable.source_config_id) <= 0)
      ) {
        toast.warn(`La variable ${variableKey} debe seleccionar una fuente API`);
        return;
      }
    }

    try {
      const payload = {
        ...form,
        estado,
        edad_min: edadMin,
        edad_max: edadMax,
        orden_visual: Number(form.orden_visual) || 0,
        schema_version: schemaVersion,
        result_config: normalizeResultConfig(form.result_config),
        variables: variables.map((variable, index) => ({
          ...variable,
          required: Number(variable.required ?? 0),
          auto_fill: Number(variable.auto_fill ?? 0),
          editable: Number(variable.editable ?? 0),
          visible: Number(variable.visible ?? 0),
          source_config_id: variable.source_config_id
            ? Number(variable.source_config_id)
            : null,
          prefill_mode: variable.prefill_mode || "none",
          allow_manual_override: Number(variable.allow_manual_override ?? 0),
          refresh_strategy: variable.refresh_strategy || "on_open",
          orden:
            variable.orden === undefined || variable.orden === null
              ? index
              : Number(variable.orden) || 0,
        })),
      };

      const rs = await calculadorasDinamicasApi.save(payload);
      if (rs.success) {
        toast.success(rs.message || "Guardado con éxito");
        await load();
        if (!form.id && rs.data?.id) {
          const created = { id: rs.data.id, nombre: payload.nombre };
          await handleSelect(created);
        }
      } else {
        toast.error(rs.message || "Error al guardar");
      }
    } catch (e) {
      toast.error("Error al guardar");
    }
  };

  const saveSource = async () => {
    if (!sourceForm.codigo.trim()) {
      toast.warn("El código de la fuente es obligatorio");
      return;
    }

    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(sourceForm.codigo.trim())) {
      toast.warn("El código de la fuente debe ser técnico");
      return;
    }

    if (!sourceForm.nombre.trim()) {
      toast.warn("El nombre de la fuente es obligatorio");
      return;
    }

    if (!sourceForm.consecutivo_id || Number(sourceForm.consecutivo_id) <= 0) {
      toast.warn("Debes seleccionar un consecutivo");
      return;
    }

    try {
      const payload = {
        ...sourceForm,
        consecutivo_id: Number(sourceForm.consecutivo_id),
        estado: Number(sourceForm.estado ?? 1),
        orden: Number(sourceForm.orden) || 0,
        allow_manual_override_default: Number(
          sourceForm.allow_manual_override_default ?? 1
        ),
      };

      const rs = await calculadorasDinamicasApi.saveSource(payload);
      if (!rs.success) {
        toast.error(rs.message || "Error guardando fuente");
        return;
      }

      toast.success(rs.message || "Fuente guardada con éxito");
      await loadSources();
      if (rs.data?.id) {
        await handleSelectSource(rs.data.id);
      } else {
        handleNewSource();
      }
    } catch (e) {
      toast.error("Error guardando fuente");
    }
  };

  return {
    items: filtered,
    search,
    setSearch,
    estadoFiltro,
    setEstadoFiltro,
    selected,
    form,
    setForm,
    handleSelect,
    handleNew,
    sourceCatalog,
    selectedSourceId,
    sourceForm,
    setSourceForm,
    consecutivos,
    handleSelectSource,
    handleNewSource,
    saveSource,
    addVariable,
    updateVariable,
    removeVariable,
    save,
    reload: load,
  };
};

